/**
 * JurisdictionWizardModal - Multi-step wizard for adding/editing jurisdictions
 * 
 * Step 1: Basic Information (Type, State, Name, Parent County, Status)
 * Step 2: Tax Collector Contact (Name, Phone, Email, Fax, Working Hours)
 * Step 3: Addresses & Details (Physical, Mailing, Websites, MLC & Workflow)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Check, ChevronLeft, ChevronRight, AlertCircle, Building2, Phone, MapPin, Loader2, History, Users, Link2, Info, Database, CalendarDays, Clock, FileText, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import './jurisdiction-wizard.css';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { getUserFromToken } from '../../utils/adminApi';
import {
  getJurisdictionTypeOptionsForState,
  getJurisdictionTypeLabelForState,
} from '../../lib/jurisdictionTaxonomy';
import JurisdictionCollectorAssignmentsTab from './JurisdictionCollectorAssignmentsTab';
import JurisdictionResearchLinksTab from './JurisdictionResearchLinksTab';
import JurisdictionMlsServicesTab from './JurisdictionMlsServicesTab';

const API = process.env.REACT_APP_BACKEND_URL;

// Step configuration
const STEPS = [
  { id: 1, title: 'Basic Info', icon: Building2 },
  { id: 2, title: 'Contact', icon: Phone },
  { id: 3, title: 'Addresses & Details', icon: MapPin },
];

// Jurisdiction types — sourced from lib/jurisdictionTaxonomy.js via
// getJurisdictionTypeOptionsForState(state): LA hides County (Parish is
// its county-equivalent level); non-LA states hide Parish.
// (Jun 2026 — Louisiana Parish vs County fix.)

// US State codes
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

// Initial form state
const getInitialFormData = (jurisdiction = null) => ({
  // Step 1 - Basic Info
  jurisdiction_type: jurisdiction?.jurisdiction_type || 'county',
  state: jurisdiction?.state || '',
  jurisdiction_name: jurisdiction?.jurisdiction_name || '',
  parent_county_id: jurisdiction?.parent_county_id || '',
  is_active: jurisdiction?.is_active ?? true,
  // MLC Required toggle - defaults to false, or true if jurisdiction has any MLC data
  mlc_required: jurisdiction?.mlc_required ?? Boolean(
    jurisdiction?.mlc_charges != null || 
    jurisdiction?.mlc_24_month_charges != null || 
    jurisdiction?.mlc_eta_days != null ||
    jurisdiction?.sase_required === true
  ),
  
  // Step 2 - Contact
  // JURDIR-PAYEE-001 (Oct 2026): explicit payee field. Stored value only — the legacy
  // tax_collector_name fallback is shown as a hint and is never written back implicitly.
  make_checks_payable_to: jurisdiction?.make_checks_payable_to || '',
  tax_collector_name: jurisdiction?.tax_collector_name || '',
  tax_collector_phone: jurisdiction?.tax_collector_phone || '',
  tax_collector_phone_ext: jurisdiction?.tax_collector_phone_ext || '',
  tax_collector_email: jurisdiction?.tax_collector_email || '',
  tax_collector_fax: jurisdiction?.tax_collector_fax || '',
  tax_collector_working_hours: jurisdiction?.tax_collector_working_hours || '',
  
  // Step 3 - Addresses & Details
  tax_collector_physical_address: jurisdiction?.tax_collector_physical_address || '',
  tax_collector_mailing_address: jurisdiction?.tax_collector_mailing_address || '',
  same_as_physical: false,
  tax_collector_website: jurisdiction?.tax_collector_website || '',
  assessor_website: jurisdiction?.assessor_website || '',
  notes: jurisdiction?.notes || '',
  
  // MLC & Workflow (only used if mlc_required is true)
  mlc_charges: jurisdiction?.mlc_charges ?? '',
  mlc_24_month_charges: jurisdiction?.mlc_24_month_charges ?? '',
  mlc_eta_days: jurisdiction?.mlc_eta_days ?? '',
  sase_required: jurisdiction?.sase_required ?? null,
  info_obtained_back: jurisdiction?.info_obtained_back || '',
});

// Stepper component (TITLEFLOW-JURISDICTION-UI-001 — presentation only; same props/testids)
const WizardStepper = ({ steps, currentStep, completedSteps }) => {
  return (
    <div className="jw-stepper w-full" data-testid="wizard-stepper">
      {steps.map((step, index) => {
        const isActive = currentStep === step.id;
        const isCompleted = completedSteps.includes(step.id);
        const Icon = step.icon;
        return (
          <React.Fragment key={step.id}>
            <div className="jw-step" data-state={isActive ? 'active' : isCompleted ? 'done' : 'idle'}>
              <div className="jw-step-dot" data-testid={`step-indicator-${step.id}`}>
                {isCompleted && !isActive ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className="jw-step-label">{step.title}</span>
            </div>
            {index < steps.length - 1 && (
              <div className="jw-step-line" data-done={completedSteps.includes(step.id) ? 'true' : 'false'} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// MLC clearing notice (MLC-STALE-DATA-CHECK-001 C → MLC-UI-CLEANUP-002): one heading + the four affected values
// in a single compact row. Shown only when an existing MLC Required = Yes is switched to No and stored values
// would be cleared on save. SASE is never part of it (independent Mail-Away dependency).
const MlcClearNotice = ({ jurisdiction }) => {
  if (jurisdiction.mlc_required !== true) return null;
  const fmtMoney = (v) => `$${Number(v).toFixed(2)}`;
  const cards = [
    ['mlc_charges', 'MLC Charges', fmtMoney, Database],
    ['mlc_24_month_charges', '24-Month MLC', fmtMoney, CalendarDays],
    ['mlc_eta_days', 'MLC ETA', (v) => `${v} ${Number(v) === 1 ? 'day' : 'days'}`, Clock],
    ['info_obtained_back', 'Info Obtained Back', (v) => String(v).charAt(0).toUpperCase() + String(v).slice(1), FileText],
  ].filter(([f]) => jurisdiction[f] !== null && jurisdiction[f] !== undefined && jurisdiction[f] !== '');
  if (!cards.length) return null;
  return (
    <div className="jw-notice" role="status" data-testid="mlc-clear-warning">
      <div className="jw-notice-head">
        <Info className="jw-notice-icon" />
        <p className="jw-notice-title">MLC information will be cleared on save</p>
      </div>
      <div className="jw-cards" data-testid="mlc-clear-warning-values">
        {cards.map(([f, label, fmt, Icon]) => (
          <div key={f} className="jw-card" data-testid={`mlc-clear-card-${f}`}>
            <Icon className="jw-card-icon" />
            <div className="min-w-0">
              <p className="jw-card-label">{label}</p>
              <p className="jw-card-value">{fmt(jurisdiction[f])}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Step 1: Basic Info
const Step1BasicInfo = ({ formData, setFormData, errors, availableCounties, loadingCounties, mode, dupCheck, justification, setJustification, onUseExisting, jurisdiction = null }) => {
  // Borough included for PA / most states. AK and NY boroughs are county-
  // equivalents and are exempted below via BOROUGH_COUNTY_EQUIVALENT_STATES.
  // Mirrors backend/models/jurisdiction.py::SUB_JURISDICTION_TYPES.
  const isSubJurisdictionType = ['city', 'town', 'township', 'village', 'school_district', 'borough'].includes(formData.jurisdiction_type);
  const PARENT_EXEMPT_STATES = ['NJ', 'RI', 'MA', 'NH', 'ME', 'VT', 'CT'];
  // May 5, 2026 — VA has 38 independent cities (Alexandria, Hampton, Richmond,
  // Virginia Beach, …) that operate outside any county. Only Type=city is
  // exempt; VA towns/villages still belong to a county. Mirrors
  // backend/models/jurisdiction.py::INDEPENDENT_CITY_STATES.
  const INDEPENDENT_CITY_STATES = ['VA'];
  // May 13, 2026 — Consolidated city-counties (e.g. Carson City, NV). Per
  // (state, name) allowlist, NOT a blanket state rule — other NV cities
  // (Las Vegas, Reno, Henderson) still belong to counties. Mirrors
  // backend/models/jurisdiction.py::CONSOLIDATED_CITY_COUNTIES.
  const CONSOLIDATED_CITY_COUNTIES = new Set(['NV|CARSON CITY']);
  // Feb 2026 — Borough is a county-equivalent in AK (19 organized boroughs)
  // and NY (the 5 NYC boroughs). Only Type=borough is exempt in these
  // states. Mirrors backend/models/jurisdiction.py::BOROUGH_COUNTY_EQUIVALENT_STATES.
  const BOROUGH_COUNTY_EQUIVALENT_STATES = ['AK', 'NY'];
  // Jun 2026 — Louisiana uses Parishes (64 parishes, zero counties). LA
  // cities may be consolidated city-parish governments (e.g. CITY OF NEW
  // ORLEANS ≡ Orleans Parish), so the parent Parish is OPTIONAL for
  // Type=city — shown and selectable, but never blocking. LA towns /
  // villages / townships / school districts still REQUIRE a parent Parish.
  // Mirrors backend/models/jurisdiction.py::PARENT_OPTIONAL_CITY_STATES.
  const PARENT_OPTIONAL_CITY_STATES = ['LA'];
  const stateUpper = (formData.state || '').toUpperCase();
  const nameUpper = (formData.jurisdiction_name || '').trim().toUpperCase();
  const isParentExemptState = PARENT_EXEMPT_STATES.includes(stateUpper);
  const isIndependentCity = INDEPENDENT_CITY_STATES.includes(stateUpper) && formData.jurisdiction_type === 'city';
  const isConsolidatedCityCounty = formData.jurisdiction_type === 'city'
    && CONSOLIDATED_CITY_COUNTIES.has(`${stateUpper}|${nameUpper}`);
  const isBoroughCountyEquivalent = formData.jurisdiction_type === 'borough'
    && BOROUGH_COUNTY_EQUIVALENT_STATES.includes(stateUpper);
  const isParentOptionalCity = formData.jurisdiction_type === 'city'
    && PARENT_OPTIONAL_CITY_STATES.includes(stateUpper)
    && !isParentExemptState;
  const requiresParent = isSubJurisdictionType && !isParentExemptState && !isIndependentCity && !isConsolidatedCityCounty && !isBoroughCountyEquivalent && !isParentOptionalCity;
  // Show the parent field as optional for LA cities (selectable, not required).
  const showParentField = requiresParent || (isSubJurisdictionType && isParentOptionalCity);
  // LA terminology — every label reads "Parish" when Louisiana is selected.
  const parentNoun = stateUpper === 'LA' ? 'Parish' : 'County';
  const parentNounLower = parentNoun.toLowerCase();
  const parentNounPlural = stateUpper === 'LA' ? 'parishes' : 'counties';
  
  const handleChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Jun 2026 — LA parish auto-switch: County and Parish are the same
      // governmental level, so flipping the State swaps the Type instead
      // of leaving an invalid (now-hidden) option selected.
      if (field === 'state') {
        const s = String(value || '').toUpperCase();
        if (s === 'LA' && next.jurisdiction_type === 'county') {
          next.jurisdiction_type = 'parish';
        } else if (s !== 'LA' && next.jurisdiction_type === 'parish') {
          next.jurisdiction_type = 'county';
        }
      }
      return next;
    });
  };
  
  return (
    <div className="space-y-6" data-testid="step-1-basic-info">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Jurisdiction Type */}
        <div className="space-y-2">
          <Label htmlFor="jurisdiction_type">Type <span className="text-red-500">*</span></Label>
          <Select
            value={formData.jurisdiction_type}
            onValueChange={(value) => handleChange('jurisdiction_type', value)}
          >
            <SelectTrigger id="jurisdiction_type" data-testid="jurisdiction-type-select">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {getJurisdictionTypeOptionsForState(formData.state).map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
              {/* Display-mapped legacy value — e.g. an LA record stored
                  as `county` renders as "Parish" but cannot be
                  re-selected once changed away. */}
              {(() => {
                const current = formData.jurisdiction_type;
                if (
                  current &&
                  !getJurisdictionTypeOptionsForState(formData.state).some((o) => o.value === current)
                ) {
                  return (
                    <SelectItem value={current} disabled>
                      {getJurisdictionTypeLabelForState(current, formData.state)}
                    </SelectItem>
                  );
                }
                return null;
              })()}
            </SelectContent>
          </Select>
          {errors.jurisdiction_type && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {errors.jurisdiction_type}
            </p>
          )}
        </div>
        
        {/* State */}
        <div className="space-y-2">
          <Label htmlFor="state">State <span className="text-red-500">*</span></Label>
          <Select
            value={formData.state}
            onValueChange={(value) => handleChange('state', value)}
          >
            <SelectTrigger id="state" data-testid="state-select">
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state} value={state}>
                  {state}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.state && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {errors.state}
            </p>
          )}
        </div>
      </div>
      
      {/* Parent County/Parish (conditional — required for most sub-jurisdictions,
          optional for LA cities like CITY OF NEW ORLEANS) */}
      {showParentField && (
        <div className="space-y-2">
          <Label htmlFor="parent_county_id">
            Parent {parentNoun} {requiresParent && <span className="text-red-500">*</span>}
            <span className="text-xs text-slate-500 ml-2">
              {requiresParent
                ? `(Required for ${formData.jurisdiction_type})`
                : '(Optional — consolidated city-parish governments may have no parent)'}
            </span>
          </Label>
          <Select
            value={formData.parent_county_id}
            onValueChange={(value) => handleChange('parent_county_id', value === '__none__' ? '' : value)}
            disabled={loadingCounties || !formData.state}
          >
            <SelectTrigger id="parent_county_id" data-testid="parent-county-select">
              <SelectValue placeholder={
                loadingCounties ? `Loading ${parentNounPlural}...` :
                !formData.state ? 'Select state first' :
                availableCounties.length === 0 ? `No ${parentNounPlural} in this state` :
                `Select parent ${parentNounLower}`
              } />
            </SelectTrigger>
            <SelectContent
              // BUG FIX (Feb 22, 2026 — Parent County dropdown jumped upward on selection):
              // The wizard modal's body is `overflow-y-auto` (see L892 of this file).
              // Radix's default `onCloseAutoFocus` calls `trigger.focus()` WITHOUT
              // `preventScroll`, so the browser's focus-scroll kicks in and re-centers
              // the trigger within the scrollable container — visually felt as the
              // dropdown/form shifting upward. Other dropdowns in the same wizard
              // don't show this because they sit near the top and don't require the
              // container to scroll before becoming visible. Preventing the default
              // and manually restoring focus with `preventScroll: true` keeps the
              // trigger keyboard-focused (a11y) but eliminates the scroll jump.
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                document.getElementById('parent_county_id')?.focus({ preventScroll: true });
              }}
            >
              {/* Optional mode (LA cities) — allow clearing back to "no parent". */}
              {!requiresParent && (
                <SelectItem value="__none__" data-testid="parent-county-none-option">
                  — None —
                </SelectItem>
              )}
              {availableCounties.map((county) => (
                <SelectItem key={county.id} value={county.id}>
                  {county.jurisdiction_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formData.state && availableCounties.length === 0 && !loadingCounties && requiresParent && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> No {parentNounPlural} found. Please add a {parentNoun} jurisdiction first.
            </p>
          )}
          {errors.parent_county_id && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {errors.parent_county_id}
            </p>
          )}
        </div>
      )}
      
      {/* Jurisdiction Name */}
      <div className="space-y-2">
        <Label htmlFor="jurisdiction_name">Jurisdiction Name <span className="text-red-500">*</span></Label>
        <Input
          id="jurisdiction_name"
          data-testid="jurisdiction-name-input"
          value={formData.jurisdiction_name}
          onChange={(e) => handleChange('jurisdiction_name', e.target.value.toUpperCase())}
          onBlur={(e) => handleChange('jurisdiction_name', e.target.value.replace(/\s+/g, ' ').trim().toUpperCase())}
          placeholder="e.g., MIAMI-DADE COUNTY"
          className="uppercase"
        />
        {errors.jurisdiction_name && (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {errors.jurisdiction_name}
          </p>
        )}

        {/* Feb 25, 2026 — Duplicate detection panel (request mode only).
            Shown beneath the name input as the user types. Exact matches
            block submission with a "Use existing instead" link. Near matches
            (≥85% similarity — hybrid canonicalized min(token_set, token_sort)
            scoring as of Feb 27, 2026 v3) require a justification. */}
        {mode === 'request' && dupCheck && (dupCheck.loading || dupCheck.exact.length > 0 || dupCheck.near.length > 0) && (
          <div data-testid="duplicate-check-panel" className="mt-2">
            {dupCheck.loading && (
              <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="duplicate-check-loading">
                <Loader2 className="w-3 h-3 animate-spin" />
                Checking directory for duplicates…
              </div>
            )}

            {!dupCheck.loading && dupCheck.exact.length > 0 && (
              <div
                className="rounded-lg border border-rose-200 bg-rose-50 p-3"
                data-testid="duplicate-exact-banner"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-rose-800">Already exists in directory</p>
                    <p className="text-xs text-rose-700 mt-0.5">
                      A jurisdiction with this name and state is already on file. Submission is blocked.
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {dupCheck.exact.map((j) => (
                        <li
                          key={j.id}
                          className="text-xs bg-white rounded border border-rose-100 px-2 py-1.5 flex items-center justify-between gap-2"
                          data-testid={`duplicate-exact-${j.id}`}
                        >
                          <span className="truncate">
                            <strong className="text-slate-800">{j.jurisdiction_name}</strong>
                            <span className="text-slate-500"> · {j.state} · {j.jurisdiction_type}</span>
                          </span>
                          <button
                            type="button"
                            onClick={onUseExisting}
                            className="text-rose-700 hover:text-rose-900 underline text-[11px] whitespace-nowrap"
                            data-testid={`use-existing-${j.id}`}
                          >
                            Use existing instead
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {!dupCheck.loading && dupCheck.exact.length === 0 && dupCheck.near.length > 0 && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                data-testid="duplicate-near-banner"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-800">Possible duplicate</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      We found similar jurisdictions in {formData.state}. If this is a new jurisdiction,
                      please provide a justification below before submitting.
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {dupCheck.near.map((j) => (
                        <li
                          key={j.id}
                          className="text-xs bg-white rounded border border-amber-100 px-2 py-1.5 flex items-center justify-between gap-2"
                          data-testid={`duplicate-near-${j.id}`}
                        >
                          <span className="truncate">
                            <strong className="text-slate-800">{j.jurisdiction_name}</strong>
                            <span className="text-slate-500"> · {j.state} · {j.jurisdiction_type}</span>
                          </span>
                          <span className="text-amber-700 font-mono text-[11px] whitespace-nowrap">
                            {j.similarity}% match
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3">
                      <Label htmlFor="dup-justification" className="text-xs font-semibold text-amber-900">
                        Justification <span className="text-rose-500">*</span>
                      </Label>
                      <Textarea
                        id="dup-justification"
                        data-testid="duplicate-justification-input"
                        rows={2}
                        value={justification || ''}
                        onChange={(e) => setJustification && setJustification(e.target.value)}
                        placeholder="e.g., This is a separate township in the same county; no overlap with the listed match."
                        className="mt-1 text-sm bg-white"
                      />
                      <p className="text-[10px] text-amber-700 mt-1">
                        Required. Min 10 characters. The Jurisdiction Manager will see this when reviewing.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Status */}
      <div className="jw-setting" data-testid="active-status-setting">
        <Switch
          id="is_active"
          data-testid="is-active-checkbox"
          className="jw-switch"
          checked={!!formData.is_active}
          onCheckedChange={(checked) => handleChange('is_active', !!checked)}
        />
        <div className="min-w-0">
          <Label htmlFor="is_active" className="jw-setting-title cursor-pointer">Active Status</Label>
          <p className="jw-setting-sub">Active jurisdictions appear in searches and dropdowns</p>
        </div>
      </div>
      
      {/* MLC Required Toggle */}
      <div className="jw-setting flex-col !items-stretch" data-testid="mlc-required-setting">
        <div className="flex items-start gap-3">
          <Switch
            id="mlc_required"
            data-testid="mlc-required-checkbox"
            className="jw-switch"
            checked={!!formData.mlc_required}
            onCheckedChange={(checked) => handleChange('mlc_required', !!checked)}
          />
          <div className="min-w-0">
            <Label htmlFor="mlc_required" className="jw-setting-title cursor-pointer">MLC Required?</Label>
            <p className="jw-setting-state" data-testid="mlc-required-state">{formData.mlc_required ? 'Yes' : 'No'}</p>
          </div>
        </div>
        {/* MLC-STALE-DATA-CHECK-001 (C): saving with MLC Required = No clears the stored MLC-dependent values */}
        {jurisdiction && !formData.mlc_required && <MlcClearNotice jurisdiction={jurisdiction} />}
      </div>
    </div>
  );
};

// Step 2: Contact & Payable Info
const Step2Contact = ({ formData, setFormData, errors }) => {
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Format phone number as (XXX) XXX-XXXX
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  
  return (
    <div className="space-y-6" data-testid="step-2-contact">
      {/* JURDIR-PAYEE-001 — Make Checks Payable To (required; legacy collector name shown as fallback only) */}
      {(() => {
        const legacyFallback = !formData.make_checks_payable_to?.trim() && formData.tax_collector_name?.trim()
          ? formData.tax_collector_name.trim() : '';
        return (
          <div className="space-y-2">
            <Label htmlFor="make_checks_payable_to">Make Checks Payable To <span className="text-red-500">*</span></Label>
            <Input
              id="make_checks_payable_to"
              data-testid="make-checks-payable-to-input"
              value={formData.make_checks_payable_to}
              onChange={(e) => handleChange('make_checks_payable_to', e.target.value.toUpperCase())}
              placeholder={legacyFallback || 'e.g., WAYNE COUNTY TREASURER'}
            />
            {legacyFallback && (
              <p className="text-xs text-amber-700 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="payee-legacy-fallback-hint">
                <span>No dedicated payee stored for this record (legacy). Payments currently fall back to the Tax Collector Name <b className="text-slate-700">{legacyFallback}</b>. Enter the correct payee to save.</span>
                <button type="button" onClick={() => handleChange('make_checks_payable_to', legacyFallback)}
                  className="text-blue-700 font-semibold hover:underline" data-testid="payee-use-legacy-name-btn">
                  Use this name as payee
                </button>
              </p>
            )}
            {errors.make_checks_payable_to && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.make_checks_payable_to}
              </p>
            )}
          </div>
        );
      })()}

      {/* Tax Collector Name (optional) */}
      <div className="space-y-2">
        <Label htmlFor="tax_collector_name">Tax Collector Name <span className="text-slate-400 font-normal">(optional)</span></Label>
        <Input
          id="tax_collector_name"
          data-testid="tax-collector-name-input"
          value={formData.tax_collector_name}
          onChange={(e) => handleChange('tax_collector_name', e.target.value.toUpperCase())}
          placeholder="e.g., WAYNE COUNTY TAX CLAIM BUREAU"
          className="uppercase"
        />
        {errors.tax_collector_name && (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {errors.tax_collector_name}
          </p>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="tax_collector_phone">Phone <span className="text-red-500">*</span></Label>
          <div className="flex gap-2">
            <Input
              id="tax_collector_phone"
              data-testid="phone-input"
              value={formData.tax_collector_phone}
              onChange={(e) => handleChange('tax_collector_phone', formatPhone(e.target.value))}
              placeholder="(302) 395-5340"
              className="flex-1"
            />
            <Input
              id="tax_collector_phone_ext"
              data-testid="phone-ext-input"
              value={formData.tax_collector_phone_ext}
              onChange={(e) => handleChange('tax_collector_phone_ext', e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Ext"
              className="w-20"
            />
          </div>
          {errors.tax_collector_phone && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {errors.tax_collector_phone}
            </p>
          )}
        </div>
        
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="tax_collector_email">Email</Label>
          <Input
            id="tax_collector_email"
            data-testid="email-input"
            type="email"
            value={formData.tax_collector_email}
            onChange={(e) => handleChange('tax_collector_email', e.target.value)}
            placeholder="taxes@county.gov"
          />
          {errors.tax_collector_email && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {errors.tax_collector_email}
            </p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Fax */}
        <div className="space-y-2">
          <Label htmlFor="tax_collector_fax">Fax</Label>
          <Input
            id="tax_collector_fax"
            data-testid="fax-input"
            value={formData.tax_collector_fax}
            onChange={(e) => handleChange('tax_collector_fax', formatPhone(e.target.value))}
            placeholder="(555) 123-4568"
          />
        </div>
        
        {/* Working Hours */}
        <div className="space-y-2">
          <Label htmlFor="tax_collector_working_hours">Working Hours</Label>
          <Input
            id="tax_collector_working_hours"
            data-testid="working-hours-input"
            value={formData.tax_collector_working_hours}
            onChange={(e) => handleChange('tax_collector_working_hours', e.target.value.toUpperCase())}
            placeholder="MON-FRI 9AM-5PM"
            className="uppercase"
          />
        </div>
      </div>
      
      <p className="text-xs text-slate-500 bg-blue-50 p-3 rounded-lg">
        💡 At least one contact method (phone or email) is required for mail-away communication.
      </p>
    </div>
  );
};

// Step 3: Addresses & Details
const Step3AddressesDetails = ({ formData, setFormData, errors, jurisdiction = null }) => {
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSameAsPhysical = (checked) => {
    setFormData(prev => ({
      ...prev,
      same_as_physical: checked,
      tax_collector_mailing_address: checked ? prev.tax_collector_physical_address : ''
    }));
  };
  
  return (
    <div className="space-y-6" data-testid="step-3-addresses">
      {/* Addresses Section */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-slate-700 border-b pb-2">Addresses</h4>
        
        {/* Physical Address */}
        <div className="space-y-2">
          <Label htmlFor="tax_collector_physical_address">Physical Address <span className="text-red-500">*</span></Label>
          <Textarea
            id="tax_collector_physical_address"
            data-testid="physical-address-input"
            value={formData.tax_collector_physical_address}
            onChange={(e) => handleChange('tax_collector_physical_address', e.target.value.toUpperCase())}
            placeholder="123 MAIN ST, SUITE 100&#10;CITY, STATE 12345"
            className="uppercase min-h-[80px]"
            rows={3}
          />
          {errors.tax_collector_physical_address && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {errors.tax_collector_physical_address}
            </p>
          )}
        </div>
        
        {/* Same as Physical checkbox */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="same_as_physical"
            data-testid="same-as-physical-checkbox"
            checked={formData.same_as_physical}
            onCheckedChange={handleSameAsPhysical}
          />
          <Label htmlFor="same_as_physical" className="cursor-pointer text-sm">
            Mailing address is same as physical address
          </Label>
        </div>
        
        {/* Mailing Address */}
        {!formData.same_as_physical && (
          <div className="space-y-2">
            <Label htmlFor="tax_collector_mailing_address">Mailing Address</Label>
            <Textarea
              id="tax_collector_mailing_address"
              data-testid="mailing-address-input"
              value={formData.tax_collector_mailing_address}
              onChange={(e) => handleChange('tax_collector_mailing_address', e.target.value.toUpperCase())}
              placeholder="PO BOX 12345&#10;CITY, STATE 12345"
              className="uppercase min-h-[80px]"
              rows={3}
            />
          </div>
        )}
      </div>
      
      {/* Websites Section */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-slate-700 border-b pb-2">Websites</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="tax_collector_website">Tax Collector Website</Label>
            <Input
              id="tax_collector_website"
              data-testid="tax-website-input"
              value={formData.tax_collector_website}
              onChange={(e) => handleChange('tax_collector_website', e.target.value)}
              placeholder="www.county.gov/taxcollector"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="assessor_website">Assessor Website</Label>
            <Input
              id="assessor_website"
              data-testid="assessor-website-input"
              value={formData.assessor_website}
              onChange={(e) => handleChange('assessor_website', e.target.value)}
              placeholder="www.county.gov/assessor"
            />
          </div>
        </div>
      </div>
      
      {/* MLC & Workflow Section — PJR-002-B: always visible; every field depends on "MLC Required?" (Step 1).
          When MLC Required = No the fields are inactive (disabled), not validated, and are not sent as changes. */}
      {(() => {
        const mlcOn = !!formData.mlc_required;
        const dis = !mlcOn;
        const disCls = 'disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed';
        return (
      <div className="space-y-4" data-testid="mlc-workflow-section" aria-disabled={dis || undefined}>
        <div className="flex items-center justify-between border-b pb-2">
          <h4 className="text-sm font-semibold text-slate-700">MLC & Workflow</h4>
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${mlcOn ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-testid="mlc-state-badge">
            {mlcOn ? 'MLC required' : 'MLC not required'}
          </span>
        </div>
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${dis ? 'opacity-60' : ''}`}>
          <div className="space-y-2">
            <Label htmlFor="mlc_charges">MLC Charges ($) {mlcOn && <span className="text-red-500">*</span>}</Label>
            <Input id="mlc_charges" data-testid="mlc-charges-input" type="number" step="0.01" min="0" disabled={dis} className={`${disCls} ${errors.mlc_charges ? 'border-red-400' : ''}`}
              value={dis ? '' : formData.mlc_charges} onChange={(e) => handleChange('mlc_charges', e.target.value)} placeholder={dis ? 'Not applicable' : 'Amount'} />
            {errors.mlc_charges && <p className="text-xs text-red-600" data-testid="mlc-charges-error">{errors.mlc_charges}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="mlc_24_month_charges">24-Month MLC ($)</Label>
            <Input id="mlc_24_month_charges" data-testid="mlc-24-month-input" type="number" step="0.01" disabled={dis} className={disCls}
              value={dis ? '' : formData.mlc_24_month_charges} onChange={(e) => handleChange('mlc_24_month_charges', e.target.value)} placeholder={dis ? 'Not applicable' : 'Amount'} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mlc_eta_days">MLC ETA (days)</Label>
            <Input id="mlc_eta_days" data-testid="mlc-eta-input" type="number" disabled={dis} className={disCls}
              value={dis ? '' : formData.mlc_eta_days} onChange={(e) => handleChange('mlc_eta_days', e.target.value)} placeholder={dis ? 'Not applicable' : 'Days'} />
          </div>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${dis ? 'opacity-60' : ''}`}>
          <div className="space-y-2">
            <Label htmlFor="info_obtained_back">Info Obtained Back {mlcOn && <span className="text-red-500">*</span>}</Label>
            <Select value={dis ? '' : (formData.info_obtained_back || '')} onValueChange={(value) => handleChange('info_obtained_back', value)} disabled={dis}>
              <SelectTrigger id="info_obtained_back" data-testid="info-obtained-select" className={`${disCls} ${errors.info_obtained_back ? 'border-red-400' : ''}`}>
                <SelectValue placeholder={dis ? 'Not applicable' : 'Select method...'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mail">Mail</SelectItem>
                <SelectItem value="fax">Fax</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
            {errors.info_obtained_back && <p className="text-xs text-red-600" data-testid="info-obtained-error">{errors.info_obtained_back}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="sase_required">SASE Required? {mlcOn && <span className="text-red-500">*</span>}</Label>
            {/* SASE-YN-001: Yes / No only (no Inherit). PJR-002-B: inactive while MLC Required = No. */}
            <div className="flex items-center gap-6 h-10" role="radiogroup" aria-label="SASE Required" aria-disabled={dis || undefined} data-testid="sase-required-select">
              {[['true', 'Yes'], ['false', 'No']].map(([v, l]) => (
                <label key={v} className={`inline-flex items-center gap-2 text-sm ${dis ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 cursor-pointer'}`}>
                  <input type="radio" name="sase_required" value={v} checked={!dis && String(formData.sase_required) === v} disabled={dis}
                    onChange={() => handleChange('sase_required', v === 'true')}
                    className="h-4 w-4 accent-indigo-600 disabled:cursor-not-allowed" data-testid={`sase-option-${l.toLowerCase()}`} />
                  {l}
                </label>
              ))}
              {dis && <span className="text-xs text-slate-400" data-testid="sase-na">Not applicable</span>}
            </div>
            {errors.sase_required && <p className="text-xs text-red-600" data-testid="sase-required-error">{errors.sase_required}</p>}
          </div>
        </div>
      </div>
        );
      })()}

      {/* Notes Section */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-slate-700 border-b pb-2">Internal Notes</h4>
        
        <div className="space-y-2">
          <Label htmlFor="notes">Notes & Special Instructions</Label>
          <Textarea
            id="notes"
            data-testid="notes-input"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value.toUpperCase())}
            placeholder="Any internal notes or special instructions..."
            className="uppercase min-h-[80px]"
            rows={3}
          />
        </div>
      </div>
    </div>
  );
};

// Main Modal Component
const JurisdictionWizardModal = ({ isOpen, onClose, jurisdiction = null, onSave, onViewActivity, mode = 'auto', defaultTab }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState(getInitialFormData(jurisdiction));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [availableCounties, setAvailableCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Feb 25, 2026 — Pre-submission duplicate detection (requestor flow only).
  // Hits GET /api/jurisdictions/search/check-duplicates after both name +
  // state are filled. `exact` matches HARD-BLOCK submission (Use existing
  // instead). `near` matches require a mandatory justification (≥10 chars).
  const [dupCheck, setDupCheck] = useState({ loading: false, exact: [], near: [] });
  const [justification, setJustification] = useState('');

  const isEditMode = !!jurisdiction;

  // May 29, 2026 — Surface 3 (Collectors & Assignments) tab strip.
  // Option B (LOCKED): show the tab ONLY for Pennsylvania jurisdictions in
  // EDIT mode. Hidden entirely for non-PA, for unsaved (create/request)
  // forms, and for the new-jurisdiction flow. See §0 Source-of-Truth Policy
  // in /app/memory/design_tax_collectors_ui.md.
  //
  // `defaultTab` lets callers (e.g. the URL-driven auto-open path from
  // the CollectorGuidanceBanner "Create Assignment" inline action) open
  // the modal with a specific tab pre-selected.
  const [activeTab, setActiveTab] = useState(
    (defaultTab === 'assignments' || defaultTab === 'research' || defaultTab === 'mls-services') ? defaultTab : 'details'
  );
  const showAssignmentsTab = (
    isEditMode
    && mode !== 'request'
    && ((jurisdiction?.state || '').toUpperCase() === 'PA')
  );
  // RESEARCH-LINKS-001 — Research Links tab: ALL states, edit mode only.
  const showResearchTab = isEditMode && mode !== 'request';
  // MLS-SVC-DIR-001 — MLS Services tab: edit mode only; county/municipal records.
  const showMlsServicesTab = isEditMode && mode !== 'request';
  const hasTabs = showAssignmentsTab || showResearchTab || showMlsServicesTab;
  // UI-004 — Research Links reports its save state so the footer can host "Save Links".
  const [researchToolbar, setResearchToolbar] = useState({ dirty: false, saving: false, loading: true, save: null });
  // Assignments tab mounts on first visit and then stays mounted (no flicker / state loss).
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab]));
  useEffect(() => {
    setVisitedTabs((prev) => (prev.has(activeTab) ? prev : new Set(prev).add(activeTab)));
  }, [activeTab]);
  const currentUser = getUserFromToken();
  
  // Reset form when modal opens/closes or jurisdiction changes
  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormData(jurisdiction));
      setCurrentStep(1);
      setCompletedSteps([]);
      setErrors({});
      setDuplicateWarning(null);
      setDupCheck({ loading: false, exact: [], near: [] });
      setJustification('');
      // Honor defaultTab when supplied by the caller (e.g. URL-driven
      // auto-open from CollectorGuidanceBanner). Falls back to 'details'
      // so existing call sites are unchanged.
      setActiveTab((defaultTab === 'assignments' || defaultTab === 'research' || defaultTab === 'mls-services') ? defaultTab : 'details');
    }
  }, [isOpen, jurisdiction, defaultTab]);

  // Feb 25, 2026 — Debounced duplicate check (request mode only).
  // We don't run this for create/edit modes — admin already has the
  // directory open and the existing pre-create unique check on the
  // backend handles those paths.
  useEffect(() => {
    if (mode !== 'request' || !isOpen) return;
    const name = (formData.jurisdiction_name || '').trim();
    const state = (formData.state || '').trim();
    if (!name || !state || name.length < 2) {
      setDupCheck({ loading: false, exact: [], near: [] });
      return;
    }
    let cancelled = false;
    setDupCheck((prev) => ({ ...prev, loading: true }));
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ name, state });
        if (formData.jurisdiction_type) params.set('jurisdiction_type', formData.jurisdiction_type);
        // May 13, 2026 — Atlanta-in-Fulton vs Atlanta-in-DeKalb fix:
        // scope sub-jurisdiction duplicate checks to the selected parent
        // county so the same name under a different parent isn't flagged
        // as an exact duplicate. The backend gates this with
        // `requires_parent_county()` so passing it for counties / VA
        // independent cities / Carson City NV is a no-op.
        if (formData.parent_county_id) {
          params.set('parent_county_id', formData.parent_county_id);
        }
        const res = await axios.get(
          `${API}/api/jurisdictions/search/check-duplicates?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) {
          setDupCheck({
            loading: false,
            exact: res.data?.exact || [],
            near: res.data?.near || [],
          });
          // Reset justification if matches changed shape (e.g. exact found —
          // submission is blocked, no point keeping a justification).
          if ((res.data?.exact || []).length > 0) setJustification('');
        }
      } catch (err) {
        if (!cancelled) setDupCheck({ loading: false, exact: [], near: [] });
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [mode, isOpen, formData.jurisdiction_name, formData.state, formData.jurisdiction_type, formData.parent_county_id]);
  
  // Fetch counties when state changes
  useEffect(() => {
    const fetchCounties = async () => {
      if (!formData.state) {
        setAvailableCounties([]);
        return;
      }
      
      setLoadingCounties(true);
      try {
        const token = localStorage.getItem('token');
        // P0 BUG FIX (Feb 2026 — Louisiana Parish missing from Parent County dropdown):
        // The previous query hard-coded `jurisdiction_type=county`, which
        // excluded LA Parishes (LA has 64 parishes, zero counties) and
        // AK/NY Boroughs (county-equivalents per
        // `BOROUGH_COUNTY_EQUIVALENT_STATES`). The new `parent_eligible=true`
        // filter is the single source of truth on the backend — it returns
        // 'county' plus any state-specific county-equivalent type per
        // `COUNTY_EQUIVALENT_TYPES_BY_STATE` in models/jurisdiction.py.
        //
        // Prior P0 fix history (Mar 1, 2026 — Wayne County dropdown bug):
        // the legacy `type=county&limit=100` aliases were silently IGNORED
        // because Pydantic expects `jurisdiction_type` + `page_size`. We
        // keep those canonical kwargs and add `active_only=true` so
        // retired records don't leak into the dropdown.
        const response = await axios.get(
          `${API}/api/jurisdictions?state=${encodeURIComponent(formData.state)}&parent_eligible=true&active_only=true&page_size=500`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAvailableCounties(response.data.jurisdictions || []);
      } catch (error) {
        console.error('Error fetching counties:', error);
        setAvailableCounties([]);
      } finally {
        setLoadingCounties(false);
      }
    };
    
    fetchCounties();
  }, [formData.state]);
  
  // Check for duplicates
  // May 13, 2026 — Atlanta-in-Fulton vs Atlanta-in-DeKalb fix: for
  // sub-jurisdictions, the duplicate test must include parent_county_id
  // (cities / towns / townships / villages / school_districts /
  // municipalities). Two records with the same (state, type, name) but
  // different parent counties are legitimate separate jurisdictions —
  // each with its own tax-collector record — and must NOT trigger the
  // inline "Duplicate jurisdiction exists" red error below the name
  // input. Mirrors the canonical `requires_parent_county()` rule on the
  // backend so the inline frontend check, the backend POST/PUT validator,
  // and `/search/check-duplicates` all agree.
  const SUB_JURISDICTION_TYPES = new Set([
    'city', 'town', 'township', 'village', 'school_district', 'municipality',
    'borough',
  ]);
  const PARENT_EXEMPT_STATES = new Set(['NJ', 'RI', 'MA', 'NH', 'ME', 'VT', 'CT']);
  const INDEPENDENT_CITY_STATES = new Set(['VA']);
  const CONSOLIDATED_CITY_COUNTIES = new Set(['NV|CARSON CITY']);
  const BOROUGH_COUNTY_EQUIVALENT_STATES = new Set(['AK', 'NY']);
  // Jun 2026 — LA cities: parent Parish is optional (CITY OF NEW ORLEANS).
  // Mirrors backend/models/jurisdiction.py::PARENT_OPTIONAL_CITY_STATES.
  const PARENT_OPTIONAL_CITY_STATES = new Set(['LA']);

  const fieldRequiresParentCounty = (type, state, name) => {
    if (!type || !state) return false;
    const t = type.trim().toLowerCase();
    const s = state.trim().toUpperCase();
    if (!SUB_JURISDICTION_TYPES.has(t)) return false;
    if (PARENT_EXEMPT_STATES.has(s)) return false;
    if (INDEPENDENT_CITY_STATES.has(s) && t === 'city') return false;
    if (PARENT_OPTIONAL_CITY_STATES.has(s) && t === 'city') return false;
    if (BOROUGH_COUNTY_EQUIVALENT_STATES.has(s) && t === 'borough') return false;
    if (t === 'city' && name) {
      if (CONSOLIDATED_CITY_COUNTIES.has(`${s}|${name.trim().toUpperCase()}`)) {
        return false;
      }
    }
    return true;
  };

  const checkDuplicate = useCallback(async () => {
    if (!formData.state || !formData.jurisdiction_name || !formData.jurisdiction_type) {
      setDuplicateWarning(null);
      return true;
    }

    try {
      const token = localStorage.getItem('token');
      // P0 BUG FIX (Mar 1, 2026): same alias/page-size issue as the
      // parent-county fetch — `type` and `limit` are NOT recognized by
      // the backend list endpoint (`jurisdiction_type` + `page_size` are).
      // The result was that the duplicate check effectively disregarded
      // the type filter and capped at 50 rows, so a real same-name
      // dupe sitting beyond row 50 would slip past the warning.
      const response = await axios.get(
        `${API}/api/jurisdictions?state=${encodeURIComponent(formData.state)}&jurisdiction_type=${encodeURIComponent(formData.jurisdiction_type)}&search=${encodeURIComponent(formData.jurisdiction_name)}&page_size=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const needsParent = fieldRequiresParentCounty(
        formData.jurisdiction_type,
        formData.state,
        formData.jurisdiction_name,
      );
      const duplicates = (response.data.jurisdictions || []).filter((j) =>
        j.jurisdiction_name.toUpperCase() === formData.jurisdiction_name.toUpperCase() &&
        (!isEditMode || j.id !== jurisdiction?.id) &&
        // For sub-jurisdictions: only flag as duplicate when the parent
        // county also matches. Two CITY OF ATLANTA records with different
        // parent counties (Fulton vs DeKalb) are legitimate and must NOT
        // collide here.
        (!needsParent || (j.parent_county_id || null) === (formData.parent_county_id || null))
      );

      if (duplicates.length > 0) {
        // Pull the parent-county hint from the matched record so the
        // user sees WHY this is a true duplicate (matches the new
        // backend error message shape).
        let parentHint = '';
        if (needsParent && duplicates[0].parent_county_name) {
          parentHint = ` under ${duplicates[0].parent_county_name}`;
        }
        setDuplicateWarning(
          `A ${formData.jurisdiction_type} named "${formData.jurisdiction_name}"${parentHint} already exists in ${formData.state}.`
        );
        return false;
      }

      setDuplicateWarning(null);
      return true;
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return true; // Allow proceeding on error
    }
  }, [formData.state, formData.jurisdiction_name, formData.jurisdiction_type, formData.parent_county_id, isEditMode, jurisdiction?.id]);
  
  // Validate current step
  const validateStep = useCallback(async (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.jurisdiction_type) newErrors.jurisdiction_type = 'Type is required';
      if (!formData.state) newErrors.state = 'State is required';
      if (!formData.jurisdiction_name?.trim()) newErrors.jurisdiction_name = 'Jurisdiction name is required';
      
      const PARENT_EXEMPT_STATES = ['NJ', 'RI', 'MA', 'NH', 'ME', 'VT', 'CT'];
      const INDEPENDENT_CITY_STATES = ['VA'];
      // May 13, 2026 — Consolidated city-counties (e.g. Carson City, NV).
      // See Step1BasicInfo above for rationale.
      const CONSOLIDATED_CITY_COUNTIES = new Set(['NV|CARSON CITY']);
      // Feb 2026 — AK/NY boroughs are county-equivalents. See Step1BasicInfo.
      const BOROUGH_COUNTY_EQUIVALENT_STATES = ['AK', 'NY'];
      const isSubType = ['city', 'town', 'township', 'village', 'school_district', 'municipality', 'borough', 'other'].includes(formData.jurisdiction_type);
      const stateUp = (formData.state || '').toUpperCase();
      const nameUp = (formData.jurisdiction_name || '').trim().toUpperCase();
      const isExemptState = PARENT_EXEMPT_STATES.includes(stateUp);
      const isIndependentCity = INDEPENDENT_CITY_STATES.includes(stateUp) && formData.jurisdiction_type === 'city';
      const isConsolidatedCityCounty = formData.jurisdiction_type === 'city'
        && CONSOLIDATED_CITY_COUNTIES.has(`${stateUp}|${nameUp}`);
      const isBoroughCountyEquivalent = formData.jurisdiction_type === 'borough'
        && BOROUGH_COUNTY_EQUIVALENT_STATES.includes(stateUp);
      // Jun 2026 — LA cities: parent Parish is optional (CITY OF NEW ORLEANS).
      // Mirrors backend PARENT_OPTIONAL_CITY_STATES.
      const isParentOptionalCity = formData.jurisdiction_type === 'city'
        && ['LA'].includes(stateUp);
      if (isSubType && !isExemptState && !isIndependentCity && !isConsolidatedCityCounty && !isBoroughCountyEquivalent && !isParentOptionalCity && !formData.parent_county_id) {
        newErrors.parent_county_id = stateUp === 'LA'
          ? 'Parent parish is required for this type'
          : 'Parent county is required for this type';
      }
      
      // Check for duplicates
      if (Object.keys(newErrors).length === 0) {
        const noDuplicate = await checkDuplicate();
        if (!noDuplicate) {
          newErrors.jurisdiction_name = 'Duplicate jurisdiction exists';
        }
      }
    }
    
    if (step === 2) {
      // JURDIR-PAYEE-002: a dedicated payee is always required — the Tax Collector Name is never an implicit substitute.
      if (!formData.make_checks_payable_to?.trim()) {
        newErrors.make_checks_payable_to = 'Make Checks Payable To is required';
      }
      if (!formData.tax_collector_phone?.trim()) newErrors.tax_collector_phone = 'Phone number is required';
      
      // Validate email format if provided
      if (formData.tax_collector_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.tax_collector_email)) {
        newErrors.tax_collector_email = 'Invalid email format';
      }
    }
    
    if (step === 3) {
      if (!formData.tax_collector_physical_address?.trim()) {
        newErrors.tax_collector_physical_address = 'Physical address is required';
      }
      // MLC-VALIDATION-FIX-001 / MLC-FEE-GT-ZERO-001: when MLC Required = Yes, MLC Charges (> $0.00), Info Obtained Back and an
      // explicit SASE Yes/No are mandatory (new and existing records). Inactive when MLC Required = No; legacy
      // null SASE is never silently converted — the user must choose.
      if (formData.mlc_required) {
        const charges = String(formData.mlc_charges ?? '').trim();
        if (!/^\d+(\.\d{1,2})?$/.test(charges) || Number(charges) <= 0) newErrors.mlc_charges = 'MLC fee must be greater than $0.00.';
        if (!['mail', 'fax', 'email'].includes(String(formData.info_obtained_back || '').toLowerCase())) {
          newErrors.info_obtained_back = 'Required.';
        }
        if (formData.sase_required === null || formData.sase_required === undefined) {
          newErrors.sase_required = 'Required.';
        }
      }
      // Website fields are now optional - no validation required
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, checkDuplicate, jurisdiction]);
  
  // Handle next step
  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      setCompletedSteps(prev => [...new Set([...prev, currentStep])]);
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };
  
  // Handle previous step
  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    const isValid = await validateStep(3);
    if (!isValid) return;

    // Feb 25, 2026 — Duplicate gating (request mode only).
    // EXACT match → hard block (defensive; UI also disables the button).
    // NEAR  match → require justification ≥10 chars.
    if (mode === 'request') {
      if (dupCheck.exact.length > 0) {
        toast.error('This jurisdiction already exists in the directory.');
        setCurrentStep(1);
        return;
      }
      if (dupCheck.near.length > 0 && justification.trim().length < 10) {
        toast.error('Please provide a justification (min 10 chars) for the possible duplicate.');
        setCurrentStep(1);
        return;
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const submitData = { ...formData };
      submitData.jurisdiction_name = (submitData.jurisdiction_name || '').replace(/\s+/g, ' ').trim();
      // JURDIR-PAYEE-001: never persist the legacy-name fallback implicitly — only an explicitly entered payee is sent.
      // tax_collector_name is optional; on create it is omitted when empty, on edit '' is an explicit clear.
      submitData.make_checks_payable_to = (submitData.make_checks_payable_to || '').trim();
      if (!submitData.make_checks_payable_to) delete submitData.make_checks_payable_to;
      submitData.tax_collector_name = (submitData.tax_collector_name || '').trim();
      if (!submitData.tax_collector_name && !(isEditMode && (jurisdiction?.tax_collector_name || '').trim())) delete submitData.tax_collector_name;

      // Attach parent_county_name (resolved from the loaded counties list)
      // alongside parent_county_id. Without this, the request review modal
      // — and any downstream consumer — has no display name for the parent
      // and falls back to showing the raw UUID. Also gives the backend a
      // reliable name-based fallback if the ID is ever stale at approval.
      if (submitData.parent_county_id) {
        const parent = (availableCounties || []).find(
          (c) => c.id === submitData.parent_county_id
        );
        if (parent?.jurisdiction_name) {
          submitData.parent_county_name = parent.jurisdiction_name;
        }
      } else {
        // Clear any leftover name when parent is intentionally unset
        // (e.g. type changed back to county).
        delete submitData.parent_county_name;
      }
      
      // Handle same_as_physical
      if (submitData.same_as_physical) {
        submitData.tax_collector_mailing_address = submitData.tax_collector_physical_address;
      }
      delete submitData.same_as_physical;
      
      // Add https:// to websites if needed
      if (submitData.tax_collector_website && !submitData.tax_collector_website.startsWith('http')) {
        submitData.tax_collector_website = `https://${submitData.tax_collector_website}`;
      }
      if (submitData.assessor_website && !submitData.assessor_website.startsWith('http')) {
        submitData.assessor_website = `https://${submitData.assessor_website}`;
      }
      
      // Handle MLC fields based on mlc_required toggle
      if (submitData.mlc_required) {
        // Convert numeric fields - use null for empty values (not empty string)
        submitData.mlc_charges = submitData.mlc_charges !== '' ? parseFloat(submitData.mlc_charges) : null;
        submitData.mlc_24_month_charges = submitData.mlc_24_month_charges !== '' ? parseFloat(submitData.mlc_24_month_charges) : null;
        submitData.mlc_eta_days = submitData.mlc_eta_days !== '' ? parseInt(submitData.mlc_eta_days) : null;
        // Keep sase_required and info_obtained_back as-is
      } else {
        // MLC not required (PJR-002-B): every dependent field is inactive → MLC values and Info Obtained Back are
        // cleared. SASE: a NEW record is left unset (follows the collector default); an EXISTING record keeps its
        // stored value untouched — the Directory PUT treats an explicit null as "clear the SASE override", which
        // would silently change Mail-Away SASE behaviour on legacy records (Aug 2026 tri-state dependency).
        submitData.mlc_charges = null;
        submitData.mlc_24_month_charges = null;
        submitData.mlc_eta_days = null;
        submitData.info_obtained_back = null;
        if (jurisdiction) delete submitData.sase_required; else submitData.sase_required = null;
      }
      
      let response;
      // Session 24 (Feb 22, 2026) — request workflow. When mode='request',
      // the wizard submits to the proposal queue instead of creating a live
      // record. Existing create/edit paths are untouched (mode='auto' uses
      // isEditMode as before).
      const isRequestMode = mode === 'request';
      if (isRequestMode) {
        // Feb 25, 2026 — attach justification + matched-IDs metadata when
        // submitting over a near-duplicate. Backend stores both fields on
        // the request doc so managers see the requestor's reasoning + the
        // exact records the requestor was warned about.
        const requestPayload = { payload: submitData };
        if (dupCheck.near.length > 0 && justification.trim()) {
          requestPayload.justification = justification.trim();
          requestPayload.near_duplicate_ids = dupCheck.near.map((j) => j.id);
        }
        response = await axios.post(
          `${API}/api/jurisdiction-requests`,
          requestPayload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Request submitted for approval');
      } else if (isEditMode) {
        response = await axios.put(
          `${API}/api/jurisdictions/${jurisdiction.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Jurisdiction updated successfully');
      } else {
        response = await axios.post(
          `${API}/api/jurisdictions`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Jurisdiction created successfully');
      }

      if (onSave) onSave(response.data);
      onClose();
    } catch (error) {
      console.error('Error saving jurisdiction:', error);
      if (isEditMode && error.response?.status === 404) {
        // JURISDICTION-DELETE-UI-001: the record was removed while this form was open — close it, refresh the list
        toast.error('This jurisdiction no longer exists — the directory will refresh');
        if (onSave) onSave(null);
        onClose();
        return;
      }
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save jurisdiction';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="jw-backdrop fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-3 sm:p-6"
      data-testid="jurisdiction-wizard-modal"
      // Backdrop click disabled to prevent accidental data loss in multi-step form
    >
      <div 
        className="jw-modal bg-white w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        data-tabbed={hasTabs ? 'true' : 'false'}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="jw-header flex items-center justify-between px-6 py-3.5">
          <h2 className="jw-title">
            {mode === 'request'
              ? 'Submit New Jurisdiction Request'
              : isEditMode
              ? 'Manage Jurisdiction'
              : 'Add New Jurisdiction'}
          </h2>
          <div className="flex items-center gap-1">
            {/* View Activity — Feb 22, 2026 Session 19. Only shown in edit mode
                (new unsaved records have no timeline yet) and only when the
                parent page wired up the handler (i.e. user has permission). */}
            {isEditMode && onViewActivity && (
              <button
                type="button"
                onClick={() => onViewActivity(jurisdiction)}
                className="jw-icon-btn"
                data-testid="wizard-view-activity-btn"
                title="View Activity Timeline"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">View Activity</span>
              </button>
            )}
            <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
            <button
              onClick={onClose}
              className="jw-icon-btn !px-2"
              data-testid="close-modal-btn"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Surface 3 — Collectors & Assignments tab strip (PA edit-mode only).
            Hidden entirely for non-PA jurisdictions per the Option B
            Source-of-Truth Policy. */}
        {hasTabs && (
          <div
            className="border-b border-slate-200 bg-white px-5"
            data-testid="jurisdiction-wizard-tab-strip"
          >
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                className="jw-tab"
                data-active={activeTab === 'details' ? 'true' : 'false'}
                data-testid="jurisdiction-tab-details"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-4 w-4" /> Details
                </span>
              </button>
              {showAssignmentsTab && (
              <button
                type="button"
                onClick={() => setActiveTab('assignments')}
                className="jw-tab"
                data-active={activeTab === 'assignments' ? 'true' : 'false'}
                data-testid="jurisdiction-tab-assignments"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-4 w-4" /> Collectors &amp; Assignments
                </span>
              </button>
              )}
              {showResearchTab && (
              <button
                type="button"
                onClick={() => setActiveTab('research')}
                className="jw-tab"
                data-active={activeTab === 'research' ? 'true' : 'false'}
                data-testid="jurisdiction-tab-research"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Link2 className="h-4 w-4" /> Research Links
                </span>
              </button>
              )}
              {showMlsServicesTab && (
              <button
                type="button"
                onClick={() => setActiveTab('mls-services')}
                className="jw-tab"
                data-active={activeTab === 'mls-services' ? 'true' : 'false'}
                data-testid="jurisdiction-tab-mls-services"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-4 w-4" /> MLS Services
                </span>
              </button>
              )}
            </div>
          </div>
        )}

        {/* Stepper (Details tab only) */}
        {(!hasTabs || activeTab === 'details') && (
          <WizardStepper
            steps={STEPS}
            currentStep={currentStep}
            completedSteps={completedSteps}
          />
        )}

        {/* Duplicate Warning (Details tab only) */}
        {duplicateWarning && (!hasTabs || activeTab === 'details') && (
          <div className="mx-6 mb-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {duplicateWarning}
          </div>
        )}

        {/* Content — secondary tabs stay mounted (hidden) so switching tabs never
            refetches, never flickers and keeps unsaved edits (UI-004). */}
        <div className="jw-body flex-1 overflow-y-auto px-6 py-4" data-active-tab={hasTabs ? activeTab : 'details'}>
          {showMlsServicesTab && (
            <div hidden={activeTab !== 'mls-services'} data-testid="jurisdiction-panel-mls-services">
              <JurisdictionMlsServicesTab jurisdiction={jurisdiction} currentUser={currentUser} />
            </div>
          )}
          {showResearchTab && (
            <div hidden={activeTab !== 'research'} data-testid="jurisdiction-panel-research">
              <JurisdictionResearchLinksTab jurisdiction={jurisdiction} onToolbar={setResearchToolbar} />
            </div>
          )}
          {showAssignmentsTab && visitedTabs.has('assignments') && (
            <div hidden={activeTab !== 'assignments'} data-testid="jurisdiction-panel-assignments">
              <JurisdictionCollectorAssignmentsTab
                jurisdiction={jurisdiction}
                currentUser={currentUser}
              />
            </div>
          )}
          {(!hasTabs || activeTab === 'details') && (
            <div key={currentStep} className="jw-step-enter">
              {currentStep === 1 && (
                <Step1BasicInfo
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  availableCounties={availableCounties}
                  loadingCounties={loadingCounties}
                  mode={mode}
                  dupCheck={dupCheck}
                  justification={justification}
                  setJustification={setJustification}
                  onUseExisting={onClose}
                  jurisdiction={jurisdiction}
                />
              )}
              {currentStep === 2 && (
                <Step2Contact
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                />
              )}
              {currentStep === 3 && (
                <Step3AddressesDetails
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  jurisdiction={jurisdiction}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer — always present so the modal frame stays stable (UI-004).
            Details: wizard navigation. Research Links: Close + Save Links.
            MLS Services / Assignments: Close only (self-managing workflows). */}
        {hasTabs && activeTab !== 'details' ? (
        <div className="jw-footer flex items-center justify-between px-6 py-3 shrink-0" data-testid="jurisdiction-tab-footer">
          <button type="button" className="jw-btn-ghost" onClick={onClose} data-testid="tab-close-btn">
            <X className="w-4 h-4" />
            Close
          </button>
          {activeTab === 'research' && (
            <button
              type="button"
              className="jw-btn-primary"
              onClick={() => researchToolbar.save && researchToolbar.save()}
              disabled={researchToolbar.saving || researchToolbar.loading || !researchToolbar.dirty}
              data-testid="research-links-save-btn"
            >
              {researchToolbar.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Links
            </button>
          )}
        </div>
        ) : (
        <div className="jw-footer flex items-center justify-between px-6 py-3 shrink-0">
          <button
            type="button"
            className="jw-btn-ghost"
            onClick={currentStep === 1 ? onClose : handleBack}
            disabled={saving}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </button>
          
          <div className="flex items-center gap-3">
            <span className="jw-step-count">
              Step {currentStep} of {STEPS.length}
            </span>
            
            {currentStep < STEPS.length ? (
              <button
                type="button"
                className="jw-btn-primary"
                onClick={handleNext}
                disabled={saving}
                data-testid="next-btn"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={
                  saving ||
                  // Feb 25, 2026 — duplicate gating for request mode.
                  // Hard-block on exact dupe; require ≥10-char justification on near.
                  (mode === 'request' && (
                    dupCheck.exact.length > 0 ||
                    (dupCheck.near.length > 0 && justification.trim().length < 10)
                  ))
                }
                className="jw-btn-primary"
                data-testid="save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {mode === 'request'
                      ? 'Submit for Approval'
                      : isEditMode
                      ? 'Update Jurisdiction'
                      : 'Create Jurisdiction'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default JurisdictionWizardModal;
